import { motion } from "framer-motion";
import { CalendarDays, MapPin, Clock } from "lucide-react";
import { assets, club, lastMatch, nextMatch } from "../../data/sep26Data";
import { Empty26 } from "./Empty26";
import { EASE_OUT, useCountdown } from "./hooks";

const Cell = ({ label, value }) => (
  <div className="border px-3 py-3 text-center" style={{ borderColor: "var(--line)", background: "rgba(7,10,28,.45)" }}>
    <div className="display text-[clamp(30px,3.6vw,46px)] tabular-nums">{String(value).padStart(2, "0")}</div>
    <div className="label mt-1" style={{ color: "var(--muted)" }}>{label}</div>
  </div>
);

export const Partido26 = () => {
  const cd = useCountdown(nextMatch.date);
  const hasDate = Boolean(nextMatch.date);
  const rival = nextMatch.rival ?? "Rival por confirmar";
  const jornada = nextMatch.jornada ? `Jornada ${nextMatch.jornada}` : null;

  return (
    <section id="partido" data-testid="s26-partido" className="section-pad">
      <div className="wrap">
        <motion.div
          className="grid overflow-hidden border lg:grid-cols-[minmax(220px,0.55fr)_1.6fr]"
          style={{ borderColor: "var(--line)" }}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE_OUT }}
        >
          <div
            className="flex flex-col justify-between gap-8 p-8 lg:p-10"
            style={{ background: "var(--guinda)" }}
          >
            <CalendarDays size={34} strokeWidth={1.5} style={{ color: "var(--gold)" }} aria-hidden="true" />
            <h2 className="display text-[clamp(40px,5vw,68px)]">
              Próximo<br />partido
            </h2>
          </div>

          <div
            className="relative grid gap-8 p-8 lg:p-12 md:grid-cols-[1fr_auto] md:items-center"
            style={{
              background: `linear-gradient(90deg, rgba(12,18,48,.94), rgba(12,18,48,.78)), url(${assets.stadium}) center/cover`,
            }}
          >
            <div>
              <div className="flex items-center gap-5 sm:gap-8">
                <img src={assets.crest} alt="Arsenal de Allende" width="84" height="99" className="h-20 w-auto sm:h-24" />
                <span className="display text-[clamp(34px,5vw,64px)]" style={{ color: "var(--gold)" }}>vs</span>
                <div
                  className="grid h-20 w-16 place-items-center border sm:h-24 sm:w-[78px]"
                  style={{ borderColor: "var(--line)" }}
                  aria-hidden="true"
                >
                  <span className="display text-2xl" style={{ color: nextMatch.rival ? "var(--bone)" : "var(--muted)" }}>
                    {nextMatch.rival ? nextMatch.rival.slice(0, 3).toUpperCase() : "?"}
                  </span>
                </div>
              </div>

              <p className="display mt-6 text-[clamp(26px,3vw,40px)]">{rival}</p>
              {jornada && <p className="label mt-1" style={{ color: "var(--gold)" }}>{jornada}</p>}

              <ul className="mt-5 space-y-2" style={{ color: "var(--muted)" }}>
                <li className="flex items-center gap-2"><Clock size={16} aria-hidden="true" /> {club.matchday}</li>
                <li className="flex items-center gap-2"><MapPin size={16} aria-hidden="true" /> {nextMatch.venue}</li>
              </ul>
            </div>

            <div data-testid="s26-countdown" className="md:min-w-[320px]">
              {hasDate && cd && !cd.done ? (
                <div className="grid grid-cols-4 gap-2" role="timer" aria-label="Cuenta regresiva al próximo partido">
                  <Cell label="Días" value={cd.d} />
                  <Cell label="Hrs" value={cd.h} />
                  <Cell label="Min" value={cd.m} />
                  <Cell label="Seg" value={cd.s} />
                </div>
              ) : (
                <p className="serif max-w-[26ch] text-xl leading-snug" style={{ color: "var(--muted)" }}>
                  {hasDate ? "Se juega esta semana." : "Fecha y hora por confirmar: el horario se sortea cada semana."}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        <div className="mt-8" data-testid="s26-ultimo-resultado">
          <h3 className="display mb-4 text-[clamp(28px,3.2vw,44px)]">Último resultado</h3>
          {lastMatch ? (
            <div className="border p-6 sm:p-10" style={{ borderColor: "var(--line)", background: "rgba(18,26,69,.5)" }}>
              <p className="label" style={{ color: "var(--gold)" }}>{lastMatch.competition}</p>
              <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-4 text-center">
                <p className="display text-[clamp(22px,3vw,40px)]">{lastMatch.home.name}</p>
                <p className="display text-[clamp(44px,7vw,96px)] tabular-nums">
                  <span style={{ color: lastMatch.home.score > lastMatch.away.score ? "var(--gold)" : undefined }}>{lastMatch.home.score}</span>
                  <span style={{ color: "var(--muted)" }}> · </span>
                  <span style={{ color: lastMatch.away.score > lastMatch.home.score ? "var(--gold)" : undefined }}>{lastMatch.away.score}</span>
                </p>
                <p className="display text-[clamp(22px,3vw,40px)]">{lastMatch.away.name}</p>
              </div>
              {lastMatch.scorers?.length > 0 && (
                <ul className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-1" style={{ color: "var(--muted)" }}>
                  {lastMatch.scorers.map((g, i) => (
                    <li key={`${g.player}-${g.minute}-${i}`}>{g.player} {g.minute}&apos;</li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <Empty26
              testId="s26-resultado-vacio"
              title="Todavía no hay resultados"
              text="El marcador del último partido aparece aquí apenas termine la siguiente jornada."
            />
          )}
        </div>
      </div>
    </section>
  );
};
